import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Plus, Trash, ArrowUp, ArrowDown, X } from "@phosphor-icons/react";

function Body({ text }) {
  const lines = (text || "").split("\n");
  const out = [];
  let bullets = [];
  const flush = () => { if (bullets.length) { out.push(<ul key={`ul-${out.length}`} className="list-disc pl-5 space-y-0.5">{bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>); bullets = []; } };
  lines.forEach((raw, i) => {
    const l = raw.trim();
    if (/^[-*•]\s+/.test(l)) { bullets.push(l.replace(/^[-*•]\s+/, "")); return; }
    flush();
    if (l) out.push(<p key={i}>{l}</p>);
  });
  flush();
  return <div className="text-sm leading-relaxed space-y-2">{out}</div>;
}

function TableView({ table }) {
  return (
    <div className="overflow-x-auto mt-3">
      <table className="w-full text-xs border border-border">
        <thead className="bg-muted/50"><tr>{table.columns.map((c, i) => <th key={i} className="text-left px-2 py-1.5 font-semibold border-b border-border">{c}</th>)}</tr></thead>
        <tbody>{table.rows.map((r, ri) => <tr key={ri} className="border-b border-border/60 last:border-0">{table.columns.map((_, ci) => <td key={ci} className="px-2 py-1.5 align-top">{r[ci]}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function TableEdit({ table, onChange, testId }) {
  const setCell = (ri, ci, v) => onChange({ ...table, rows: table.rows.map((r, i) => (i === ri ? r.map((c, j) => (j === ci ? v : c)) : r)) });
  return (
    <div className="overflow-x-auto mt-3" data-testid={testId}>
      <table className="w-full text-xs border border-border">
        <thead className="bg-muted/50"><tr>{table.columns.map((c, i) => <th key={i} className="text-left px-2 py-1.5 font-semibold border-b border-border">{c}</th>)}<th className="w-8" /></tr></thead>
        <tbody>
          {table.rows.map((r, ri) => (
            <tr key={ri} className="border-b border-border/60 last:border-0">
              {table.columns.map((_, ci) => <td key={ci} className="p-1 align-top"><Textarea value={r[ci] || ""} onChange={e => setCell(ri, ci, e.target.value)} rows={1} className="text-xs min-h-[30px] py-1" /></td>)}
              <td className="p-1 align-top"><button type="button" className="text-muted-foreground hover:text-red-600" onClick={() => onChange({ ...table, rows: table.rows.filter((_, i) => i !== ri) })} title="Remove row"><X size={13} /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button size="sm" variant="ghost" className="mt-1 h-7 text-xs" onClick={() => onChange({ ...table, rows: [...table.rows, table.columns.map(() => "")] })}><Plus size={12} className="mr-1" /> Add row</Button>
    </div>
  );
}

/** Summary, key points and the numbered sections — read view for approved documents, inline editing for drafts. */
export default function HseSectionEditor({ doc, editable, onChange }) {
  const sections = doc.sections || [];
  const setSec = (i, patch) => onChange({ sections: sections.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const move = (i, d) => { const n = [...sections]; const [s] = n.splice(i, 1); n.splice(i + d, 0, s); onChange({ sections: n }); };
  const add = () => onChange({ sections: [...sections, { id: Math.random().toString(36).slice(2, 10), heading: "New section", body: "", table: null }] });
  const points = doc.key_points || [];

  return (
    <div className="space-y-4" data-testid="hse-section-editor">
      <section className="bg-card border border-border rounded-lg p-5" data-testid="hse-doc-summary">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Summary</p>
        {editable ? <Textarea value={doc.summary || ""} onChange={e => onChange({ summary: e.target.value })} rows={3} className="text-sm" data-testid="hse-doc-summary-input" /> : <Body text={doc.summary} />}
        {(points.length > 0 || editable) && (
          <div className="mt-4">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">Key points</p>
            {editable ? (
              <div className="space-y-1.5">
                {points.map((p, i) => (
                  <div key={i} className="flex gap-1.5"><Input value={p} onChange={e => onChange({ key_points: points.map((x, j) => (j === i ? e.target.value : x)) })} className="h-8 text-sm" data-testid={`hse-doc-point-${i}`} />
                    <button type="button" className="text-muted-foreground hover:text-red-600" onClick={() => onChange({ key_points: points.filter((_, j) => j !== i) })}><X size={14} /></button></div>
                ))}
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onChange({ key_points: [...points, ""] })}><Plus size={12} className="mr-1" /> Add point</Button>
              </div>
            ) : <ul className="list-disc pl-5 text-sm space-y-0.5">{points.map((p, i) => <li key={i}>{p}</li>)}</ul>}
          </div>
        )}
      </section>

      {sections.map((s, i) => (
        <section key={s.id} className="bg-card border border-border rounded-lg p-5" data-testid={`hse-doc-section-${i}`}>
          <div className="flex items-start gap-2 mb-3">
            <span className="text-xs font-bold text-primary mt-2">{i + 1}.</span>
            {editable ? <Input value={s.heading} onChange={e => setSec(i, { heading: e.target.value })} className="h-9 font-[Chivo] font-semibold" data-testid={`hse-doc-section-heading-${i}`} />
              : <h2 className="font-[Chivo] font-semibold text-base flex-1">{s.heading}</h2>}
            {editable && (
              <div className="flex gap-0.5 shrink-0">
                <Button size="icon" variant="ghost" className="h-8 w-8" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp size={14} /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8" disabled={i === sections.length - 1} onClick={() => move(i, 1)}><ArrowDown size={14} /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => onChange({ sections: sections.filter((_, j) => j !== i) })} data-testid={`hse-doc-section-delete-${i}`}><Trash size={14} /></Button>
              </div>
            )}
          </div>
          {editable ? <Textarea value={s.body} onChange={e => setSec(i, { body: e.target.value })} rows={Math.min(18, Math.max(5, Math.ceil((s.body || "").length / 110)))} className="text-sm leading-relaxed" data-testid={`hse-doc-section-body-${i}`} /> : <Body text={s.body} />}
          {s.table && (editable ? <TableEdit table={s.table} onChange={t => setSec(i, { table: t })} testId={`hse-doc-section-table-${i}`} /> : <TableView table={s.table} />)}
        </section>
      ))}
      {editable && <Button variant="outline" size="sm" onClick={add} data-testid="hse-doc-add-section"><Plus size={14} className="mr-1" /> Add section</Button>}
    </div>
  );
}
